/**
 * LinkedIn audience by facet — followers or Page views, per masthead (CM plan
 * §8b, P6-4/P6-6). The source is a LIFETIME snapshot collected daily (the API
 * has no demographic breakdown for a time window), so the panel reads the
 * latest snapshot and, once a snapshot 30 days older exists, the change over
 * those 30 days. Facet values arrive as Standardized Data ids; names come
 * from the labels table.
 *
 * Follower facets are public-safe with a small-cell floor (P6-2): cells under
 * the catalog's floor are withheld here exactly as the public view withholds
 * them. Page-view facets are internal.
 */
import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getFacetLabels, getMetricSeries, type MetricDefinition, type SeriesRow } from '../../lib/api/analytics.js';
import { addDays, toNumber } from '../../lib/analyticsShape.js';
import { CrawlerTable, type CrawlerRow } from './CrawlerTable.js';

const FACETS: Array<{ key: string; label: string; kind: string | null }> = [
  { key: 'country', label: 'Country', kind: 'geo' },
  { key: 'industry', label: 'Industry', kind: 'industry' },
  { key: 'function', label: 'Job function', kind: 'function' },
  { key: 'seniority', label: 'Seniority', kind: 'seniority' },
  { key: 'market_area', label: 'Market area', kind: 'geo' },
  { key: 'staff_count', label: 'Company size', kind: null },
];

const SOURCES = [
  { metric: 'li.followers.demographic', label: 'Followers', value: 'Followers' },
  { metric: 'li.page.views_demographic', label: 'Page views', value: 'Views' },
] as const;

const TOP_N = 8;

/** 'SIZE_2_TO_10' → '2–10 staff', 'SIZE_10001_OR_MORE' → '10,001+ staff', 'SIZE_1' → '1 staff'. */
export function staffCountLabel(v: string): string {
  const n = (s: string) => Number(s).toLocaleString();
  let m = /^SIZE_(\d+)_TO_(\d+)$/.exec(v);
  if (m) return `${n(m[1])}–${n(m[2])} staff`;
  m = /^SIZE_(\d+)_OR_MORE$/.exec(v);
  if (m) return `${n(m[1])}+ staff`;
  m = /^SIZE_(\d+)$/.exec(v);
  return m ? `${n(m[1])} staff` : v;
}

/**
 * Rows of the latest snapshot per site, and of the snapshot 30 days before it.
 *
 * Read from TWO narrow windows, never one 31-day window: the series endpoint
 * caps at 5,000 rows in date order, and a daily snapshot of up to 100 values
 * per facet x 3 Pages passes that in ~17 days — the NEWEST rows would be the
 * ones cut, so "latest" would silently be days old.
 */
export function snapshots(latestRows: SeriesRow[], baselineRows: SeriesRow[] = []): { latest: SeriesRow[]; baseline: SeriesRow[]; asAt: string | null; baselineAt: string | null } {
  const rows = latestRows;
  const lastBySite = new Map<string, string>();
  for (const r of rows) {
    const prev = lastBySite.get(r.site);
    if (!prev || r.metric_date > prev) lastBySite.set(r.site, r.metric_date);
  }
  const latest = rows.filter((r) => lastBySite.get(r.site) === r.metric_date);
  const baseline = baselineRows.filter((r) => {
    const last = lastBySite.get(r.site);
    return last != null && r.metric_date === addDays(last, -30);
  });
  const asAt = Array.from(lastBySite.values()).sort().pop() ?? null;
  const baselineAt = baseline.length ? baseline[0].metric_date : null;
  return { latest, baseline, asAt, baselineAt };
}

export interface LinkedInFacetsProps {
  sites?: string[];
  to: string;
  stale?: boolean;
  defs: Map<string, MetricDefinition>;
}

export function LinkedInFacets({ sites, to, stale, defs }: LinkedInFacetsProps) {
  const [facet, setFacet] = useState('country');
  const [sourceIdx, setSourceIdx] = useState(0);
  const source = SOURCES[sourceIdx];
  const def = defs.get(source.metric);
  const floor = toNumber(def?.public_min_value ?? null);
  const publicSafe = def?.visibility === 'public_safe';
  const kind = FACETS.find((f) => f.key === facet)?.kind ?? null;

  // Two 3-day windows (a missed sweep still leaves a snapshot in each).
  const series = useQuery({
    queryKey: ['analytics-li-facets', source.metric, facet, to, sites],
    queryFn: () => getMetricSeries({ metric: source.metric, sites, dateFrom: addDays(to, -2), dateTo: to, dimension: facet }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const baselineSeries = useQuery({
    queryKey: ['analytics-li-facets-baseline', source.metric, facet, to, sites],
    queryFn: () => getMetricSeries({ metric: source.metric, sites, dateFrom: addDays(to, -32), dateTo: addDays(to, -28), dimension: facet }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const labels = useQuery({ queryKey: ['analytics-li-labels'], queryFn: getFacetLabels, staleTime: 10 * 60_000 });

  const { rows, withheld, asAt, baselineAt, siteKeys } = useMemo(() => {
    const snap = snapshots(series.data?.rows ?? [], baselineSeries.data?.rows ?? []);
    const name = (id: string) => {
      if (facet === 'staff_count') return staffCountLabel(id);
      return (kind && labels.data?.[kind]?.[id]) || `unlabelled (${id})`;
    };
    const base = new Map<string, number>();
    for (const r of snap.baseline) base.set(`${r.site}|${r.dimension_value}`, (toNumber(r.value) ?? 0));
    const acc = new Map<string, CrawlerRow & { change: number | null }>();
    let withheldCount = 0;
    for (const r of snap.latest) {
      const v = toNumber(r.value) ?? 0;
      // The public-safe floor, applied per cell as v_public_metric_facts does.
      if (publicSafe && floor != null && v < floor) { withheldCount += 1; continue; }
      const key = name(r.dimension_value);
      const e = acc.get(key) ?? { key, total: 0, bySite: {}, estimated: false, change: snap.baseline.length ? 0 : null };
      e.total += v;
      e.bySite[r.site] = (e.bySite[r.site] ?? 0) + v;
      if (e.change != null) e.change += v - (base.get(`${r.site}|${r.dimension_value}`) ?? 0);
      acc.set(key, e);
    }
    const sorted = Array.from(acc.values()).sort((a, b) => b.total - a.total).slice(0, TOP_N);
    return {
      rows: sorted.map((e) => ({
        ...e,
        key: e.change != null && e.change !== 0 ? `${e.key} (${e.change > 0 ? '+' : ''}${e.change.toLocaleString()} in 30 days)` : e.key,
      })),
      withheld: withheldCount,
      asAt: snap.asAt,
      baselineAt: snap.baselineAt,
      siteKeys: Array.from(new Set(snap.latest.map((r) => r.site))),
    };
  }, [series.data, baselineSeries.data, labels.data, facet, kind, publicSafe, floor]);

  const basis = baselineAt ? `lifetime, with the change since ${baselineAt}` : 'demographics · lifetime';
  const floorLine = publicSafe && floor != null
    ? ` · cells under ${floor} withheld (public-safe floor)${withheld ? `: ${withheld} withheld` : ''}`
    : '';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="LinkedIn facet">
        <div className="flex items-center gap-1 rounded-full border border-grey-200 bg-surface p-1">
          {SOURCES.map((s, i) => (
            <button
              key={s.metric}
              type="button"
              onClick={() => setSourceIdx(i)}
              aria-pressed={sourceIdx === i}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                sourceIdx === i ? 'bg-primary-600 text-white' : 'text-grey-600 hover:bg-grey-100'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-grey-600">
          <span className="sr-only">Facet</span>
          <select
            value={facet}
            onChange={(e) => setFacet(e.target.value)}
            className="rounded-full border border-grey-200 bg-surface px-3 py-1.5 text-xs font-medium text-grey-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            {FACETS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
      </div>
      <CrawlerTable
        rows={rows}
        sites={siteKeys}
        from={asAt ?? to}
        to={asAt ?? to}
        stale={stale || series.isFetching}
        title={`LinkedIn ${source.label.toLowerCase()} by ${FACETS.find((f) => f.key === facet)?.label.toLowerCase()}`}
        note={`${basis}${asAt ? ` · as at ${asAt}` : ''}. Top ${TOP_N}; LinkedIn returns the top 100 per facet.`}
        keyLabel={FACETS.find((f) => f.key === facet)?.label ?? 'Facet'}
        valueLabel={source.value}
        emptyText={series.isLoading ? 'Loading…' : 'No LinkedIn snapshot yet — the lane collects daily once enabled'}
        footer={`LinkedIn Community Management API · aggregate counts for our own Pages, no member data${floorLine}`}
        visibility={publicSafe ? 'public_safe' : 'internal'}
      />
    </div>
  );
}
